import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';

import { trackHubClickPerson } from '../../analytics';

import { Logo } from '../../components/general';
import { PageWrapper } from '../../components/layout/content';
import { ErrorModal } from '../../components/modals';

import { URL } from '../../constants';

import { getCreationResults } from '../../services/api/creations';
import { getEventPublic } from '../../services/api/events';
import { getTemplateData } from '../../services/api/template';

import { PublicEvent, Person, Shot, PageError, ModalError } from '../../types';
import { isEmpty, round, getEventImg, getUrl, getShareLink } from '../../utils';

import ShareFooter from './components/shareFooter';

const ShareHub = () => {
  const { eventId = '' } = useParams();

  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [generatedFaces, setGeneratedFaces] = useState<Person[]>([]);
  const [requiredShots, setRequiredShots] = useState<Shot[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState<PageError>(null);
  const [error, setError] = useState<ModalError>(null);

  useEffect(() => {
    const onMount = async () => {
      setIsLoading(true);

      await fetchEventData();

      setIsLoading(false);
    };

    onMount();
  }, [eventId]);

  const fetchEventData = async () => {
    try {
      const event = await getEventPublic(eventId);

      if (!event) {
        setPageError(404);
        return;
      }

      setEvent(event);

      const generatedFaces = await getCreationResults(eventId, true);

      if (isEmpty(generatedFaces)) {
        setError({
          heading: 'No videos generated',
          subheading:
            'It looks like no videos have been generated yet for this event. Please check back later!',
        });
        return;
      }

      const { requiredShots } = await getTemplateData(eventId, true);

      setGeneratedFaces(generatedFaces);
      setRequiredShots(requiredShots);
    } catch (err) {
      console.error(err);
      setError({ subheading: 'Error fetching the generated videos' });
    }
  };

  const eventName = event?.name ?? 'Event';

  return (
    <PageWrapper
      type="full-page"
      isLoading={isLoading}
      pageError={pageError}
      title={eventName}
      description={`${eventName} hub page with personalised highlight reels`}
      className="px-[5vw] sm:px-[7.5vw] pt-[5vw] sm:pt-7"
      noTips
    >
      <div className="flex flex-col px-5 sm:px-20 py-7 gap-4 sm:gap-8 bg-grey-100 text-center rounded-md shadow-grey-1">
        <Logo withTagLine />

        <h2>
          <b>{eventName}</b> has created {generatedFaces.length} personalised
          videos!
        </h2>

        <hr className="my-4" />

        <div className="flex flex-col items-center gap-7">
          {event?.logo && (
            <img
              src={getEventImg('logo', event.logo)}
              alt="Logo"
              className="w-[50vw] sm:w-[13vw] aspect-square object-cover rounded-md"
            />
          )}

          <h4>Click faces to find videos for:</h4>

          <div className="flex flex-wrap justify-center gap-6">
            {generatedFaces.map((faceObj) => {
              const face = faceObj.face;

              return (
                <Link
                  to={getShareLink(eventId, face.elementId)}
                  onClick={() => trackHubClickPerson(face.name)}
                  target="_blank"
                  className="w-[calc((100%_-_(1.5rem_*_2))_/_3)] sm:w-[calc((100%_-_(1.5rem_*_5))_/_6)] text-center no-underline transition-all duration-500 hover:scale-110 hover:opacity-100"
                >
                  <img
                    src={faceObj.img}
                    className="w-full aspect-square rounded-full object-cover"
                    alt={`${face.name} Icon`}
                  />

                  <div className="w-full mt-2 text-black whitespace-nowrap overflow-hidden text-ellipsis">
                    {face.name}
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="text-xs italic">
            Checked in but your video isn’t here?{' '}
            <Link
              to={`${getUrl(URL.CHECK_IN, eventId)}?reqAssets=clips-${requiredShots.map((clip) => round(clip.duration)).join(',')}`}
              target="_blank"
            >
              Help us create it
            </Link>
          </div>
        </div>
      </div>

      <ShareFooter type="share_hub" />

      <ErrorModal
        show={!!error}
        heading={error?.heading ?? 'Something went wrong'}
        subheading={error?.subheading}
        isTryAgainBtn
      />
    </PageWrapper>
  );
};

export default ShareHub;
